import { useEffect, useState } from "react";
import { STAGE_A_APPORTER } from "@/data/site";
import { track, trackGoogleAdsConversion } from "@/lib/analytics";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatCHF } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useNoIndex } from "@/lib/seo";
import { useSeo } from "@/lib/seo";

const longDate = (iso) => new Intl.DateTimeFormat("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(iso.length > 10 ? iso : `${iso}T12:00:00`));

const PaymentSuccess = () => {
  useSeo({ title: "Paiement confirmé · Entre Les Mondes", description: "", noindex: true });
  useNoIndex();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("polling");
  const [result, setResult] = useState(null);
  useEffect(() => {
    const d = result?.data;
    if (d && (result.kind === "booking" || result.kind === "solde") && (d.event_type === "stage" || d.type === "individuel")) {
      track("complete_drum_booking", { mode: d.type === "individuel" ? "individuel" : "groupe", payment: "en_ligne" });
      trackGoogleAdsConversion({ transactionId: d.transaction_id, value: d.amount_paid });
    }
  }, [result]);

  useEffect(() => {
    document.title = "Paiement confirmé — Entre les Mondes";
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const s = await api.paymentStatus(sessionId);
        if (s.payment_status === "paid") {
          const r = await api.paymentResult(sessionId);
          setResult(r);
          if (r.kind === "order") clear();
          setState("paid");
          return;
        }
        if (["expirée", "annulée", "échouée"].includes(s.payment_status)) { setState("failed"); return; }
      } catch (e) { /* retry */ }
      attempts += 1;
      if (attempts >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state === "polling") {
    return (
      <div className="pt-44 pb-32 text-center" data-testid="payment-polling">
        <Loader2 className="w-10 h-10 text-terra mx-auto animate-spin mb-6" />
        <p className="font-serif text-2xl text-bark">Vérification de votre paiement…</p>
        <p className="text-fumee mt-2 text-sm">Un instant, nous confirmons la transaction auprès de Stripe.</p>
      </div>
    );
  }

  if (state !== "paid") {
    const msgs = {
      failed: "Le paiement n'a pas été finalisé. Votre place ou votre tambour n'ont pas été débités.",
      timeout: "Le paiement est en cours de traitement. Vous recevrez un courriel dès qu'il sera confirmé.",
      error: "Aucune transaction à afficher.",
    };
    return (
      <div className="pt-44 pb-32 text-center max-w-xl mx-auto px-6" data-testid="payment-not-paid">
        <p className="font-serif text-3xl text-bark mb-4">{state === "timeout" ? "Encore un instant" : "Paiement non confirmé"}</p>
        <p className="text-fumee mb-8">{msgs[state]}</p>
        <Link to="/agenda" data-testid="payment-back-agenda" className="link-line text-terra">Retour à l'agenda</Link>
      </div>
    );
  }

  const isBooking = result.kind === "booking" || result.kind === "solde";
  const isStage = isBooking && (result.data.event_type === "stage" || result.data.type === "individuel");
  const isGift = result.kind === "giftcard";
  const d = result.data;
  const acompteEnCours = isBooking && d.acompte && d.solde_statut === "à régler";
  const prenom = (isBooking ? d.nom : isGift ? d.acheteur_nom : d.client.nom).split(" ")[0];
  const email = isBooking ? d.email : isGift ? d.acheteur_email : d.client.email;

  return (
    <div className="pt-40 pb-24">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <CheckCircle2 className="w-16 h-16 text-sage mx-auto mb-8" />
        </motion.div>
        <p className="eyebrow mb-4">{acompteEnCours ? "Acompte reçu — place bloquée" : isBooking ? "Réservation confirmée" : isGift ? "Carte cadeau émise" : "Commande confirmée"}</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-bark leading-tight" data-testid="payment-success-title">Merci, {prenom}.</h1>
        <p className="mt-5 text-fumee leading-relaxed">
          Votre paiement est confirmé — référence <span className="font-mono text-terra" data-testid="payment-success-ref">{isBooking ? d.reference : d.numero}</span>.
          {isGift && d.destinataire_email ? ` Le code a été envoyé à ${d.destinataire_email} et une copie arrive vers ${email}.` : ` Un courriel de confirmation arrive vers ${email}.`}
        </p>
        <div className="mt-10 bg-card border border-sand-dark rounded-2xl p-8 text-left space-y-3" data-testid="payment-success-recap">
          {isGift ? (
            <>
              <p className="font-medium text-bark">Carte cadeau Entre Les Mondes — {formatCHF(d.montant)} × {d.quantite}</p>
              <div className="space-y-2 pt-2">
                {d.cartes.map((c) => (
                  <div key={c.code} className="flex items-center justify-between rounded-xl bg-sand-dark/50 px-4 py-3" data-testid="giftcard-code">
                    <span className="font-mono tracking-[0.2em] text-bark">{c.code}</span>
                    <span className="text-sm text-terra">{formatCHF(c.solde)}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-fumee pt-2">Valable un an — à saisir dans le champ « Carte cadeau » lors d'une réservation ou d'une commande.</p>
            </>
          ) : isBooking ? (
            <>
              <p className="font-medium text-bark">{d.event_titre}</p>
              <p className={`text-sm text-fumee ${d.type === "wyrd" ? "" : "capitalize"}`}>{d.type === "wyrd" ? d.periode_label : `${longDate(d.event_date)}${d.date_individuelle ? ", 10 h – 18 h" : d.horaire ? `, ${d.horaire}` : ""}`}</p>
              <p className="text-sm text-fumee">{d.type === "lieu" ? `${d.type_lieu_label} · ${d.adresse_lieu}` : d.type === "consultation" ? `${d.mode_label}` : d.lieu}</p>
              {d.peau && (
                <p className="text-sm text-terra font-mono tracking-wide">
                  Peau de {d.peau.toLowerCase()} · forme {(d.forme || "ronde").toLowerCase()} · {d.diametre} cm{d.lacage ? ` · laçage ${d.lacage}` : ""}
                </p>
              )}
              <div className="flex justify-between items-baseline pt-3 border-t border-sand-dark">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-fumee">{d.type === "wyrd" ? "parcours de 6 mois" : d.type === "lieu" ? "visite de 2 h" : d.type === "consultation" ? `consultation ${d.duree_min} min` : `${d.participants} place(s)`} · {d.formule === "mensuel" ? `1re mensualité de ${formatCHF(d.mensualite)} payée · ${d.mensualites} × ${formatCHF(d.mensualite)}` : acompteEnCours ? `acompte ${formatCHF(d.acompte)} payé` : "payé en ligne"}{d.remise_cadeau ? ` + carte cadeau ${formatCHF(d.remise_cadeau)}` : ""}</span>
                <span className="font-serif text-2xl text-terra">{formatCHF(d.montant)}</span>
              </div>
              {acompteEnCours && (
                <div className="rounded-xl bg-terra/10 border border-terra/30 p-4 text-sm text-bark" data-testid="payment-success-solde">
                  Solde de <strong>{formatCHF(d.solde_restant)}</strong> à régler au plus tard le jour {d.type === "lieu" ? "de la visite" : "du stage"} (<span className="capitalize">{longDate(d.solde_echeance)}</span>), avant le début {d.type === "lieu" ? "de la visite" : "de l'atelier"} — un lien vous a été envoyé par courriel.{" "}
                  <Link to={`/solde/${d.id}`} className="link-line text-terra" data-testid="payment-success-solde-link">Régler maintenant</Link>
                </div>
              )}
            </>
          ) : (
            <>
              {d.lignes.map((l) => (
                <div key={l.product_id} className="flex justify-between text-sm">
                  <span className="text-bark">{l.nom} <span className="text-fumee">({l.numero})</span></span>
                  <span className="text-bark">{formatCHF(l.prix)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm text-fumee pt-3 border-t border-sand-dark"><span>Livraison</span><span>Offerte</span></div>
              <div className="flex justify-between items-baseline">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-fumee">Total payé en ligne</span>
                <span className="font-serif text-2xl text-terra">{formatCHF(d.total)}</span>
              </div>
            </>
          )}
        </div>
        {isStage && (
          <div className="mt-6 bg-terra/10 border border-terra/30 rounded-2xl p-6 text-left" data-testid="payment-success-stage">
            <p className="eyebrow mb-3">Ce qu'il faut prévoir</p>
            <ul className="space-y-1.5 text-sm text-bark">{STAGE_A_APPORTER.map((a) => <li key={a}>· {a}</li>)}</ul>
            <p className="text-xs text-fumee mt-4">Réservation confirmée. Les détails pratiques (adresse de l'atelier, parking, rappel à J-3) vous arrivent par e-mail.</p>
          </div>
        )}
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          {isStage && <Link to="/offres/cercles-evenements" data-testid="payment-success-cercles" className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-wood/30 text-bark text-sm hover:border-terra hover:text-terra transition-colors duration-300">Découvrir les cercles de tambours</Link>}
          <Link to={isBooking ? "/agenda" : isGift ? "/" : "/boutique"} data-testid="payment-success-cta"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors duration-300">
            Continuer la visite <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
